import React, { useCallback, useContext } from 'react';
import { gql } from '@apollo/client';
import { toast } from 'react-toastify';
import Lottie from 'react-lottie-player';

import AmountPaid from 'Components/AmountPaid';
import TabInvoicesTable from '../TabOpen/components/TabInvoicesTable/TabInvoicesTable';
import animationData from 'Shared/spiner_blue.json';
import getTotalAmountPaid from 'Shared/util/getTotalAmountPaid';

import { ReactComponent as LogoLight } from '../../assets/invoicesOpenEmpty.svg';
import { ReactComponent as LogoDark } from '../../assets/InvoicesOpenEmptyDark.svg';

import EmptyPage from 'Components/EmptyPage/EmptyPage';

import StoreContext from 'Shared/context/StoreContext';
import useQueryByContract from 'Shared/hooks/useQueryByContract';
import withErrorBoundary from 'Components/ErrorBoundary/ErrorBoundary';
import { logger } from 'RootShared/utility/ErrorLogger';
import useDocumentTitle from 'Shared/hooks/useDocumentTitle';

export const queryGetInvoicesOpen = gql`
  query TabinvoicesOpenQuery($contractId: Int!) @api(name: root) {
    invoices(status: OPEN, contractId: $contractId) {
      reference
      id
      title
      currency
      status
      subject
      createdAt
      dueDate
      total
      pdfUrl
    }
  }
`;

const TabOpen = () => {
  const { contract } = useContext(StoreContext);
  const { loading, data, error } = useQueryByContract(contract, queryGetInvoicesOpen);

  useDocumentTitle('Invoices - Open');

  const formatingForTable = useCallback((invoices) => {
    let invoicesForTable = [];

    invoicesForTable = invoices.map(({ reference, subject, createdAt, dueDate, total, currency, pdfUrl, id }) => {
      return {
        invoice: { reference: reference, slug: subject },
        issued: { date: createdAt },
        due_date: { date: dueDate },
        amount: { number: total, currency: currency },
        download: { url: pdfUrl },
        id: id,
      };
    });

    return invoicesForTable;
  }, []);

  const getInvoicesOpenComponent = useCallback(
    (data) => {
      if (data.invoices.length === 0) {
        return (
          <EmptyPage
            img={JSON.parse(localStorage.getItem('nightMode')) ? <LogoDark /> : <LogoLight />}
            title={'No open invoices yet'}
            text={"Looks like you haven't had pending invoices yet."}
          />
        );
      }

      return (
        <>
          <AmountPaid title={'Total amount to be paid'} amount={getTotalAmountPaid(data.invoices)} />
          <TabInvoicesTable invoices={formatingForTable(data.invoices)} />
        </>
      );
    },
    [formatingForTable]
  );

  if (error) {
    logger.error(error);
    toast(error.message, {
      type: toast.TYPE.ERROR,
      autoClose: 3000,
      closeButton: true,
      hideProgressBar: false,
    });
  }

  return (
    <div className="container tab-invoices tab-invoices-open" data-testid="tab-invoices-open">
      {loading && (
        <Lottie
          className="lottie"
          loop={true}
          play={true}
          animationData={animationData}
          width={100}
          style={{ width: 100, height: 100, margin: '0px auto' }}
        />
      )}
      {data && getInvoicesOpenComponent(data)}
    </div>
  );
};

export default withErrorBoundary(TabOpen);
